import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { format, parseISO } from 'date-fns'
import { Lock, Users, Globe, Trash2, Edit3, ChevronLeft,
         MapPin, Mic, Play, Pause, Video } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useDeleteEntry } from '@/hooks/useData'
import ImageViewer from '@/components/ui/ImageViewer'
import type { Entry } from '@/types/database'

const PRIVACY_ICON = { private: Lock, friends: Users, public: Globe }

const TAG_COLORS: Record<string, string> = {
  birthday:    'bg-rose/10 text-rose border-rose/20',
  travel:      'bg-blue/10 text-blue-light border-blue/20',
  milestone:   'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  journal:     'bg-purple/10 text-purple border-purple/20',
  family:      'bg-green/10 text-green border-green/20',
  work:        'bg-teal/10 text-teal border-teal/20',
  health:      'bg-green/10 text-green border-green/20',
  celebration: 'bg-rose/10 text-rose border-rose/20',
  nature:      'bg-green/10 text-green border-green/20',
}

function mediaType(url: string): 'photo' | 'video' | 'voice' {
  const l = url.toLowerCase()
  if (l.includes('.mp3') || l.includes('.m4a') || l.includes('.ogg') ||
      l.includes('.webm') || l.includes('audio') || l.includes('voice')) return 'voice'
  if (l.includes('.mp4') || l.includes('.mov') || l.includes('.avi') ||
      l.includes('video')) return 'video'
  return 'photo'
}

// ── Robust audio player ───────────────────────────────
function AudioPlayer({ url }: { url: string }) {
  const [playing,  setPlaying]  = useState(false)
  const [progress, setProgress] = useState(0)
  const [duration, setDuration] = useState(0)
  const [loaded,   setLoaded]   = useState(false)
  const [errored,  setErrored]  = useState(false)
  const ref = useRef<HTMLAudioElement>(null)

  const toggle = async () => {
    if (!ref.current || errored) return
    try {
      if (playing) {
        ref.current.pause()
        setPlaying(false)
      } else {
        await ref.current.play()
        setPlaying(true)
      }
    } catch (e) {
      setErrored(true)
    }
  }

  const fmt = (s: number) =>
    isNaN(s) ? '0:00' : `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`

  if (errored) {
    return (
      <div className="flex items-center gap-2 p-3 rounded-xl bg-white/5 border border-white/10">
        <Mic size={16} className="text-white/30" />
        <span className="text-xs text-white/30 font-mono">
          Voice note — open in browser to play
        </span>
        <a href={url} target="_blank" rel="noreferrer"
          className="ml-auto text-xs text-blue-light underline">Open</a>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-purple/10 border border-purple/20">
      <audio
        ref={ref}
        preload="metadata"
        onLoadedMetadata={() => { setDuration(ref.current?.duration ?? 0); setLoaded(true) }}
        onTimeUpdate={() => setProgress(ref.current?.currentTime ?? 0)}
        onEnded={() => { setPlaying(false); setProgress(0) }}
        onError={() => setErrored(true)}
      >
        <source src={url} />
      </audio>
      <button onClick={toggle}
        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ background: 'linear-gradient(135deg,#8b5cf6,#6d28d9)' }}>
        {playing
          ? <Pause size={16} className="text-white" />
          : <Play  size={16} className="text-white ml-0.5" />}
      </button>
      <div className="flex-1">
        <input type="range" min={0} max={duration || 100} value={progress}
          onChange={e => {
            if (ref.current) { ref.current.currentTime = +e.target.value; setProgress(+e.target.value) }
          }}
          className="w-full h-1 accent-purple"
          disabled={!loaded}
        />
        <div className="flex justify-between mt-0.5">
          <span className="font-mono text-[10px] text-white/30">{fmt(progress)}</span>
          <span className="font-mono text-[10px] text-white/30">{loaded ? fmt(duration) : '...'}</span>
        </div>
      </div>
      <Mic size={14} className="text-purple flex-shrink-0" />
    </div>
  )
}

export default function EntryCard({ entry }: { entry: Entry }) {
  const navigate    = useNavigate()
  const deleteEntry = useDeleteEntry()
  const [open, setOpen]               = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [viewerIndex, setViewerIndex] = useState<number | null>(null)

  const PrivIcon  = PRIVACY_ICON[entry.privacy]
  const allUrls   = entry.media_urls ?? []
  const photoUrls = allUrls.filter(u => mediaType(u) === 'photo')
  const videoUrls = allUrls.filter(u => mediaType(u) === 'video')
  const voiceUrls = allUrls.filter(u => mediaType(u) === 'voice')
  const firstPhoto = photoUrls[0]

  const handleDelete = async () => {
    await deleteEntry.mutateAsync(entry.id)
    setOpen(false); setConfirmDelete(false)
  }

  const openEditor = () => {
    setOpen(false)
    navigate('/timeline/edit', { state: { entry } })
  }

  return (
    <>
      {/* ── Card ─────────────────────────────────────── */}
      <div onClick={() => setOpen(true)}
        className={`card mb-3 cursor-pointer active:scale-[0.98] transition-all duration-150
          ${entry.privacy === 'private' ? 'border-l-2 border-l-purple' : ''}
          ${entry.is_milestone ? 'border-yellow-500/30' : ''}`}>

        {firstPhoto && (
          <div className="relative">
            <img src={firstPhoto} alt="" className="w-full h-48 object-cover" />
            <div className="absolute bottom-2 right-2 flex gap-1.5">
              {videoUrls.length > 0 && (
                <div className="bg-black/60 backdrop-blur-sm rounded-full px-2.5 py-1 font-mono text-xs text-white flex items-center gap-1">
                  <Video size={10} /> {videoUrls.length}
                </div>
              )}
              {voiceUrls.length > 0 && (
                <div className="bg-black/60 backdrop-blur-sm rounded-full px-2.5 py-1 font-mono text-xs text-white flex items-center gap-1">
                  <Mic size={10} /> {voiceUrls.length}
                </div>
              )}
              {photoUrls.length > 1 && (
                <div className="bg-black/60 backdrop-blur-sm rounded-full px-2.5 py-1 font-mono text-xs text-white">
                  +{photoUrls.length - 1}
                </div>
              )}
            </div>
          </div>
        )}

        {!firstPhoto && videoUrls.length > 0 && (
          <div className="h-20 bg-black flex items-center justify-center gap-2">
            <Video size={24} className="text-white/30" />
            <span className="font-mono text-xs text-white/30">{videoUrls.length} video{videoUrls.length > 1 ? 's' : ''}</span>
          </div>
        )}

        {!firstPhoto && videoUrls.length === 0 && voiceUrls.length > 0 && (
          <div className="h-10 bg-purple/5 border-b border-white/[0.05] flex items-center px-4 gap-2">
            <Mic size={13} className="text-purple" />
            <span className="font-mono text-xs text-white/30">
              {voiceUrls.length} voice note{voiceUrls.length > 1 ? 's' : ''} attached
            </span>
          </div>
        )}

        <div className="p-4">
          <div className="flex items-start justify-between gap-2 mb-1.5">
            <div className="flex items-center gap-1.5 flex-wrap">
              {entry.is_milestone && <span className="text-xs text-yellow-400 font-medium">🏆 Milestone</span>}
              {entry.tags?.slice(0, 3).map(tag => (
                <span key={tag}
                  className={`text-[10px] px-2 py-0.5 rounded-full border font-medium
                    ${TAG_COLORS[tag.toLowerCase()] ?? 'bg-white/5 text-white/40 border-white/10'}`}>
                  {tag}
                </span>
              ))}
            </div>
            <PrivIcon size={13} className="text-white/20 flex-shrink-0 mt-0.5" />
          </div>
          <h3 className="font-semibold text-white text-[15px] leading-snug mb-1">{entry.title}</h3>
          {entry.body && (
            <p className="text-white/50 text-sm leading-relaxed line-clamp-2">{entry.body}</p>
          )}
          <div className="flex items-center gap-3 mt-3 pt-3 border-t border-white/[0.05]">
            <span className="font-mono text-[10px] text-white/25">
              {format(parseISO(entry.recorded_at), 'MMM d, yyyy · h:mm a')}
            </span>
            {entry.location_name && (
              <span className="font-mono text-[10px] text-white/25 flex items-center gap-1">
                <MapPin size={9} /> {entry.location_name}
              </span>
            )}
            <span className="ml-auto font-mono text-[10px] text-white/20">Tap to view →</span>
          </div>
        </div>
      </div>

      {/* ── Full Entry View ───────────────────────────── */}
      <AnimatePresence>
        {open && (
          <motion.div className="fixed inset-0 z-50 flex flex-col bg-bg overflow-y-auto"
            initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }} transition={{ duration: 0.22 }}>

            <div className="glass sticky top-0 z-10 px-5 py-4 safe-top flex items-center justify-between">
              <button onClick={() => { setOpen(false); setConfirmDelete(false) }}
                className="flex items-center gap-1 text-white/50">
                <ChevronLeft size={20} /><span className="text-sm">Back</span>
              </button>
              <div className="flex gap-2">
                <button onClick={openEditor}
                  className="w-9 h-9 rounded-xl bg-blue/10 border border-blue/20 flex items-center justify-center">
                  <Edit3 size={16} className="text-blue-light" />
                </button>
                <button onClick={() => setConfirmDelete(true)}
                  className="w-9 h-9 rounded-xl bg-rose/10 border border-rose/20 flex items-center justify-center">
                  <Trash2 size={16} className="text-rose" />
                </button>
              </div>
            </div>

            {/* Photo grid */}
            {photoUrls.length > 0 && (
              <div className={`grid gap-0.5 ${photoUrls.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                {photoUrls.map((url, i) => (
                  <div key={url} onClick={() => setViewerIndex(i)}
                    className={`overflow-hidden cursor-zoom-in active:opacity-90 relative
                      ${photoUrls.length === 1 ? 'h-80' : 'h-44'}
                      ${i === 0 && photoUrls.length === 3 ? 'col-span-2' : ''}`}>
                    <img src={url} alt="" className="w-full h-full object-cover" />
                    {i === 0 && (
                      <div className="absolute bottom-2 right-2 bg-black/50 backdrop-blur-sm rounded-full px-2 py-1 font-mono text-[9px] text-white/60">
                        Tap to expand
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="px-5 py-6 flex flex-col gap-4">
              <h1 className="font-serif text-3xl font-bold text-white leading-tight">
                {entry.title}
              </h1>

              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-xs text-white/30">
                  {format(parseISO(entry.recorded_at), 'EEEE, MMMM d, yyyy')}
                </span>
                <span className="text-white/15">·</span>
                <span className="font-mono text-xs text-white/30">
                  {format(parseISO(entry.recorded_at), 'h:mm a')}
                </span>
                {entry.location_name && (
                  <><span className="text-white/15">·</span>
                  <span className="flex items-center gap-1 font-mono text-xs text-white/30">
                    <MapPin size={10} /> {entry.location_name}
                  </span></>
                )}
              </div>

              <div className="flex gap-2 flex-wrap">
                <span className={`flex items-center gap-1.5 text-xs px-3 py-1 rounded-full border font-mono
                  ${entry.privacy === 'private' ? 'border-purple/30 text-purple bg-purple/10' :
                    entry.privacy === 'friends'  ? 'border-blue/30 text-blue-light bg-blue/10' :
                    'border-green/30 text-green bg-green/10'}`}>
                  <PrivIcon size={11} />
                  {entry.privacy.charAt(0).toUpperCase() + entry.privacy.slice(1)}
                </span>
                <span className="text-xs px-3 py-1 rounded-full border border-white/10 text-white/40 font-mono capitalize">
                  {entry.entry_type}
                </span>
                {entry.is_milestone && (
                  <span className="text-xs px-3 py-1 rounded-full border border-yellow-500/30 text-yellow-400 bg-yellow-500/10">
                    🏆 Milestone
                  </span>
                )}
              </div>

              {entry.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {entry.tags.map(tag => (
                    <span key={tag}
                      className={`text-xs px-3 py-1 rounded-full border font-medium
                        ${TAG_COLORS[tag.toLowerCase()] ?? 'bg-white/5 text-white/40 border-white/10'}`}>
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              <div className="h-px bg-white/[0.07]" />

              {entry.body
                ? <p className="text-white/70 text-base leading-relaxed whitespace-pre-wrap">{entry.body}</p>
                : <p className="text-white/20 text-sm italic">No additional notes.</p>}

              {/* Videos */}
              {videoUrls.length > 0 && (
                <div>
                  <p className="font-mono text-[10px] text-white/30 tracking-widest uppercase mb-2">
                    🎥 Videos
                  </p>
                  <div className="flex flex-col gap-3">
                    {videoUrls.map(url => (
                      <video key={url} src={url} controls
                        className="w-full rounded-xl bg-black max-h-64"
                        playsInline />
                    ))}
                  </div>
                </div>
              )}

              {/* Voice notes */}
              {voiceUrls.length > 0 && (
                <div>
                  <p className="font-mono text-[10px] text-white/30 tracking-widest uppercase mb-2">
                    🎙 Voice Notes
                  </p>
                  <div className="flex flex-col gap-2">
                    {voiceUrls.map(url => (
                      <AudioPlayer key={url} url={url} />
                    ))}
                  </div>
                </div>
              )}

              <button onClick={openEditor}
                className="mt-2 w-full py-3.5 rounded-2xl border border-blue/20 bg-blue/5 text-blue-light font-semibold text-sm flex items-center justify-center gap-2">
                <Edit3 size={16} /> Edit This Memory
              </button>
            </div>

            <AnimatePresence>
              {confirmDelete && (
                <motion.div className="fixed inset-0 bg-black/70 z-20 flex items-end"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  onClick={() => setConfirmDelete(false)}>
                  <motion.div className="w-full bg-surface rounded-t-3xl p-6 safe-bottom"
                    initial={{ y: 100 }} animate={{ y: 0 }} exit={{ y: 100 }}
                    onClick={e => e.stopPropagation()}>
                    <h3 className="font-serif text-2xl font-bold text-white text-center mb-2">
                      Delete this memory?
                    </h3>
                    <p className="text-white/40 text-sm text-center mb-6 leading-relaxed">
                      This cannot be undone. All photos, videos, and voice notes will be permanently removed.
                    </p>
                    <div className="flex flex-col gap-3">
                      <button onClick={handleDelete} disabled={deleteEntry.isPending}
                        className="w-full py-4 rounded-2xl font-semibold text-rose border border-rose/30 bg-rose/10 active:scale-95 transition-all">
                        {deleteEntry.isPending ? 'Deleting…' : 'Yes, Delete Forever'}
                      </button>
                      <button onClick={() => setConfirmDelete(false)} className="btn-secondary">
                        Cancel — Keep This Memory
                      </button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {viewerIndex !== null && (
          <ImageViewer
            urls={photoUrls}
            startIndex={viewerIndex}
            onClose={() => setViewerIndex(null)}
          />
        )}
      </AnimatePresence>
    </>
  )
}
